import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { permissions, roles, type Permission, type Role } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import {
  alternarCaixa,
  chavesDasCaixas,
  estadoDaCaixa,
  gruposDoPerfilDaCorretora,
  telasIniciaisDaCorretora,
  type CaixaDoPerfil,
} from './perfilDaCorretora';
import { Trilha } from './Trilha';

/**
 * Caixa de marcar de tres estados: marcada, desmarcada ou pela metade (o perfil tem
 * parte das permissoes por tras da caixa).
 */
function CaixaDoOnPoint({
  caixa,
  marcadas,
  editavel,
  disponiveis,
  onChange,
}: {
  caixa: CaixaDoPerfil;
  marcadas: Set<string>;
  editavel: boolean;
  disponiveis: Set<string>;
  onChange?: (proximo: Set<string>) => void;
}) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  const estado = estadoDaCaixa(caixa, marcadas);
  const semChave =
    caixa.chaves.length === 0 || caixa.chaves.some((chave) => !disponiveis.has(chave));
  const bloqueada = !editavel || caixa.indisponivel !== undefined || semChave;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = estado === 'parcial';
  }, [estado]);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className="size-4 accent-[var(--color-brand-500)]"
          checked={estado === 'marcada'}
          disabled={bloqueada}
          onChange={() => onChange?.(alternarCaixa(caixa, marcadas, disponiveis))}
        />
        <label htmlFor={id} className="text-sm text-ink-800">
          {caixa.rotulo}
        </label>
        {estado === 'parcial' ? <Badge tone="warning">Em parte</Badge> : null}
      </div>
      {editavel && caixa.indisponivel ? (
        <p className="pl-6 text-2xs text-ink-500">{caixa.indisponivel}</p>
      ) : null}
      {editavel && !caixa.indisponivel && caixa.nota ? (
        <p className="pl-6 text-2xs text-ink-500">{caixa.nota}</p>
      ) : null}
    </div>
  );
}

/** As permissoes da corretora que nenhuma caixa do OnPoint cobre. */
function outrasDoSeguraCore(catalogo: Permission[]): Permission[] {
  const cobertas = chavesDasCaixas();
  return catalogo.filter((item) => !cobertas.has(item.key));
}

/**
 * Formulario dos dialogos "Cadastrar perfil" e "Editar perfil" do OnPoint: Nome, as
 * caixas por grupo, "Habilitar filtro mandatorio", "Home menu", Cancelar e Salvar.
 *
 * Salvar grava tudo numa chamada so (POST ou PATCH de /roles), com o teto de
 * concessao no servidor: quem nao tem uma permissao nao a marca.
 */
function DialogoDoPerfil({
  perfil,
  catalogo,
  onClose,
}: {
  perfil: Role | null;
  catalogo: Permission[];
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const disponiveis = new Set(catalogo.map((item) => item.key));

  const [nome, setNome] = useState(perfil?.name ?? '');
  const [marcadas, setMarcadas] = useState<Set<string>>(
    new Set((perfil?.permissions ?? []).filter((chave) => disponiveis.has(chave))),
  );
  const [telaInicial, setTelaInicial] = useState(perfil?.home_route ?? '');
  const imutavel = perfil?.is_immutable === true;

  const telas = telasIniciaisDaCorretora(marcadas);
  const opcoesDaTela =
    telaInicial !== '' && !telas.some((tela) => tela.value === telaInicial)
      ? [...telas, { value: telaInicial, label: `${telaInicial} (atual)` }]
      : telas;

  const salvar = useMutation({
    mutationFn: () => {
      const chaves = [...marcadas];
      const home = telaInicial || undefined;
      if (perfil) {
        return roles.update(
          perfil.id,
          imutavel
            ? { ...(home ? { home_route: home } : {}) }
            : {
                name: nome,
                permissions: chaves,
                ...(home ? { home_route: home } : {}),
              },
        );
      }
      return roles.create({
        name: nome,
        broker_scoped: true,
        permissions: chaves,
        ...(home ? { home_route: home } : {}),
      });
    },
    onSuccess: (gravado) => {
      toast.success(
        perfil
          ? `Perfil ${gravado.name} atualizado.`
          : `Perfil ${gravado.name} cadastrado.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['session'] });
      onClose();
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  function alternarOutra(chave: string, marcado: boolean) {
    setMarcadas((atual) => {
      const proximo = new Set(atual);
      if (marcado) proximo.add(chave);
      else proximo.delete(chave);
      return proximo;
    });
  }

  const outras = outrasDoSeguraCore(catalogo);

  return (
    <Modal
      open
      onClose={onClose}
      title={perfil ? 'Editar perfil' : 'Cadastrar perfil'}
      description={
        perfil
          ? `Configurar perfil ${perfil.name}`
          : 'Adicionar perfil de acesso da corretora'
      }
      size="lg"
    >
      <form onSubmit={enviar} className="flex flex-col gap-5">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar o perfil">
            {salvar.error.message}
          </Alert>
        ) : null}
        {imutavel ? (
          <Alert tone="info" title="Perfil imutavel">
            As permissoes dele acompanham a plataforma. So o Home menu pode ser trocado.
          </Alert>
        ) : null}

        <Field
          label="Nome"
          required
          maxLength={60}
          placeholder="Digite o nome do perfil"
          disabled={imutavel}
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
        />

        {gruposDoPerfilDaCorretora.map((grupo) => (
          <fieldset key={grupo.titulo} className="flex flex-col gap-2">
            <legend className="text-xs font-semibold text-ink-800">
              {grupo.titulo}
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {grupo.caixas.map((caixa) => (
                <CaixaDoOnPoint
                  key={caixa.rotulo}
                  caixa={caixa}
                  marcadas={marcadas}
                  editavel={!imutavel}
                  disponiveis={disponiveis}
                  onChange={setMarcadas}
                />
              ))}
            </div>
          </fieldset>
        ))}

        {outras.length > 0 ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-semibold text-ink-800">
              Outras permissoes do SeguraCore
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {outras.map((item) => (
                <label
                  key={item.key}
                  className="flex items-start gap-2 text-xs text-ink-700"
                >
                  <input
                    type="checkbox"
                    disabled={imutavel}
                    checked={marcadas.has(item.key)}
                    onChange={(evento) =>
                      alternarOutra(item.key, evento.target.checked)
                    }
                    className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
                  />
                  <span className="min-w-0">
                    <span className="block">{item.description || item.key}</span>
                    <span className="block text-2xs text-ink-400">{item.key}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        {telas.length === 0 && telaInicial === '' ? (
          <p className="text-2xs text-ink-500">
            Atribua alguma funcionalidade ao perfil para selecionar o home.
          </p>
        ) : (
          <Select
            label="Home menu"
            placeholder="Primeira tela do menu da corretora"
            value={telaInicial}
            options={opcoesDaTela}
            onChange={(evento) => setTelaInicial(evento.target.value)}
          />
        )}

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvar.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Quadro de um perfil da corretora, com as caixas do OnPoint so para leitura. */
function QuadroDoPerfil({
  perfil,
  catalogo,
  podeConfigurar,
  onConfigurar,
}: {
  perfil: Role;
  catalogo: Permission[];
  podeConfigurar: boolean;
  onConfigurar: () => void;
}) {
  const disponiveis = new Set(catalogo.map((item) => item.key));
  const marcadas = new Set(perfil.permissions);
  const outras = outrasDoSeguraCore(catalogo).filter((item) => marcadas.has(item.key));

  return (
    <Card>
      <CardHeader
        title={perfil.name}
        {...(perfil.description ? { description: perfil.description } : {})}
        action={
          <div className="flex flex-wrap gap-1.5">
            {perfil.is_default ? <Badge tone="brand">Padrao</Badge> : null}
            {perfil.is_assignable ? null : <Badge tone="warning">Nao atribuivel</Badge>}
          </div>
        }
      />
      <CardBody className="flex flex-col gap-4">
        <p className="text-2xs text-ink-500">
          Home menu: <span className="font-mono">{perfil.home_route || '/'}</span>
        </p>
        {gruposDoPerfilDaCorretora.map((grupo) => {
          const caixas = grupo.caixas.filter((caixa) => !caixa.soNoDialogo);
          if (caixas.length === 0) return null;
          return (
            <section key={grupo.titulo} aria-label={`Permissoes - ${grupo.titulo}`}>
              <h4 className="text-xs font-semibold text-ink-800">
                Permissoes - {grupo.titulo}
              </h4>
              <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
                {caixas.map((caixa) => (
                  <CaixaDoOnPoint
                    key={caixa.rotulo}
                    caixa={caixa}
                    marcadas={marcadas}
                    editavel={false}
                    disponiveis={disponiveis}
                  />
                ))}
              </div>
            </section>
          );
        })}
        {outras.length > 0 ? (
          <section aria-label="Outras permissoes do SeguraCore">
            <h4 className="text-xs font-semibold text-ink-800">
              Outras permissoes do SeguraCore
            </h4>
            <ul className="mt-1 list-disc pl-5 text-2xs text-ink-600">
              {outras.map((item) => (
                <li key={item.key}>{item.description || item.key}</li>
              ))}
            </ul>
          </section>
        ) : null}
        {podeConfigurar ? (
          <div className="flex justify-end border-t border-line pt-3">
            <Button size="sm" variant="secondary" onClick={onConfigurar}>
              Configurar perfil {perfil.name}
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * Perfil da Corretora (`/parametrizacao/perfil-corretora`), a tela
 * `/parametrizacao/broker-profile` do OnPoint.
 *
 * Mostra os perfis da corretora (os papeis com escopo de corretora) com as caixas do
 * OnPoint, nos grupos dele, so para leitura, e abre "Cadastrar perfil" e "Editar
 * perfil". A matriz completa de todos os perfis continua em Usuarios e perfis.
 *
 * Os perfis sao os do SeguraCore ate a matriz de perfis da seguradora chegar
 * (CFG-002): a tela traz a estrutura do OnPoint sem mudar o que cada perfil concede.
 */
export function BrokerProfilePage() {
  const { can } = useAuth();
  const podeLer = can('usuarios.papel.view');
  const podeCriar = can('usuarios.papel.create') && can('usuarios.papel.update');
  const podeConfigurar = can('usuarios.papel.update');

  const [dialogo, setDialogo] = useState<{ perfil: Role | null } | null>(null);

  const perfis = useQuery({
    queryKey: ['roles', 'corretora'],
    queryFn: ({ signal }) => roles.list(signal, 'corretora'),
    enabled: podeLer,
  });
  const catalogo = useQuery({
    queryKey: ['permissions', 'corretora'],
    queryFn: ({ signal }) => permissions.list(signal, 'corretora'),
    enabled: podeLer,
  });

  const cabecalho = (
    <>
      <Trilha
        itens={[{ rotulo: 'Parametrizacao', para: '/parametrizacao' }]}
        atual="Perfil da Corretora"
      />
      <PageHeader
        title="Perfil da Corretora"
        description="Perfis de acesso da corretora e o que cada um pode fazer."
        actions={
          podeLer && podeCriar ? (
            <Button size="sm" onClick={() => setDialogo({ perfil: null })}>
              Adicionar perfil de acesso
            </Button>
          ) : undefined
        }
      />
    </>
  );

  if (!podeLer) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Alert tone="info" title="Sem acesso aos perfis">
          O seu acesso nao inclui a leitura dos perfis (usuarios.papel.view).
        </Alert>
      </div>
    );
  }

  if (perfis.isPending || catalogo.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (perfis.isError || catalogo.isError) {
    return (
      <div className="flex flex-col gap-4">
        {cabecalho}
        <Alert tone="danger" title="Nao foi possivel carregar os perfis da corretora">
          {(perfis.error ?? catalogo.error)?.message}
        </Alert>
      </div>
    );
  }

  // O administrador da corretora vem primeiro, como o "Administrador" do OnPoint.
  const lista = [...perfis.data].sort((a, b) => {
    const admA = a.key === 'corretor_parceiro_admin' ? 0 : 1;
    const admB = b.key === 'corretor_parceiro_admin' ? 0 : 1;
    return admA === admB ? a.name.localeCompare(b.name) : admA - admB;
  });

  return (
    <div className="flex flex-col gap-4">
      {cabecalho}
      {lista.length === 0 ? (
        <Alert tone="info" title="Nenhum perfil da corretora">
          Use Adicionar perfil de acesso para cadastrar o primeiro.
        </Alert>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {lista.map((perfil) => (
            <QuadroDoPerfil
              key={perfil.id}
              perfil={perfil}
              catalogo={catalogo.data}
              podeConfigurar={podeConfigurar}
              onConfigurar={() => setDialogo({ perfil })}
            />
          ))}
        </div>
      )}
      {dialogo ? (
        <DialogoDoPerfil
          perfil={dialogo.perfil}
          catalogo={catalogo.data}
          onClose={() => setDialogo(null)}
        />
      ) : null}
    </div>
  );
}
